import React, {Component} from 'react';
import styled from 'styled-components';
import {SortableHandle} from 'react-sortable-hoc';

import {areShallowEqual} from '../../lib/helpers';
import {parseVariables} from '../../lib/json-logic-utils';
import Card from '../../components/Card';
import Input from '../../components/Input';
import Icon from '../../components/Icon';
import JsonLogicBuilder from '../../components/JsonLogicBuilder';
import AlertDialog from '../../components/AlertDialog';
import Button from '../../components/Button';
import Menu from '../../components/Menu';

import {createQuestionOption, getLogicBuilderVariables, hasChoices, createQuestion} from './shared';
import DeletableCard from './DeletableCard';
import SubConfiguration from './SubConfiguration';
import OptionsBuilder from './OptionsBuilder';
import DefaultAnswers from './DefaultAnswers';
import { isValidCssColor } from '../../lib/color';

import NumericGridBuilder from './NumericGridBuilder';

class QuestionBuilder extends Component {
  state = {
    editCondition: !!this.props.question.condition,
    editDefaultAnswers: this.props.question.defaultAnswers.length > 0,
    restrictCatalogTypes: getQuestionMetadata(this.props.question, 'selectedTypes').length > 0,
    validationEnabled: false,
    dialog: null,
    optionsOpen: false,
    showAdvancedSettings: hasAdvancedSettings(this.props.question),
    allowSaveAnswers: getQuestionMetadata(this.props.question, 'allowSaveAnswers').includes('false') ? false : true,
    gridWidth: parseInt(getQuestionMetadata(this.props.question, 'gridWidth')[0]) || 2,
    gridHeight: parseInt(getQuestionMetadata(this.props.question, 'gridHeight')[0]) || 2,
    displayInline: getQuestionMetadata(this.props.question, 'displayInline').includes('true'),
    questionWidth: getQuestionMetadata(this.props.question, 'questionWidth')[0] || '1',
    questionColour: getQuestionMetadata(this.props.question, 'questionColour')[0] || '#000000',
    cardColour: getQuestionMetadata(this.props.question, 'cardColour')[0] || '#FFFFFF'
  };

  shouldComponentUpdate(prevProps, prevState) {
    const {question, disableDelete} = this.props;
    
    if (prevState !== this.state) {
      // Always update when the local state is updated.
      return true;
    }

    if (prevProps.disableDelete !== disableDelete) {
      // Always update when the disableDelete prop is changed.
      return true;
    }

    if (prevProps.question !== question) {
      // Always update when the question prop is changed.
      return true;
    }

    // Checking validations is more involved because you need to check the entire object.
    const {questionOptionsByValue: prevQuestionOptionsByValue, ...prevValidations} = prevProps.validations;
    const {validations: {questionOptionsByValue, ...validations}} = this.props;

    if (!areShallowEqual(prevValidations, validations) || !areShallowEqual(prevQuestionOptionsByValue, questionOptionsByValue)) {
      // Always update if the validations have changed.
      return true;
    }

    return false;
  }

  closeAlertDialog = () => {
    this.setState({dialog: null});
  }

  openAlertDialog = props => {
    this.setState({dialog: props});
  }

  handleConfirmDelete = () => {
    const {form, question} = this.props;

    // Deleting the question is not allowed if this question is referenced in a condition or alert expression.
    const dependentQuestions = getDependentQuestions(form, question);

    if (dependentQuestions.length > 0) {
      this.openAlertDialog({
        title: `Can't delete this question!`,
        description: `This question is used by the condition for the "${dependentQuestions[0].text}" question.`
      });

      return false;
    }

    const dependentAlerts = getDependentAlerts(form, question);

    if (dependentAlerts.length > 0) {
      this.openAlertDialog({
        title: `Can't delete this question!`,
        description: `This question is used by the condition for the "${dependentAlerts[0].name}" alert.`
      });

      return false;
    }

    const dependentElements = getDependentElements(form, question);

    if (dependentElements.length > 0) {
      this.openAlertDialog({
        title: `Can't delete this question!`,
        description: `This question is used by the "${dependentElements[0].data.label}" element.`
      });

      return false;
    }

    return true;
  }

  handleRemove = () => {
    const {question, onRemove} = this.props;
    onRemove(question);
  }

  setQuestionState = (reducer, onComplete) => {
    const {question, onChange} = this.props;

    onChange({
      ...question,
      ...(typeof reducer === 'function' ? reducer(question) : reducer)
    }, onComplete);
  }

  handleQuestionTypeChange = e => {
    const {form, question} = this.props;

    // Changing the question type is not allowed if this question is referenced in a condition or alert expression.
    const dependentQuestions = getDependentQuestions(form, question);

    if (dependentQuestions.length > 0) {
      return this.openAlertDialog({
        title: `Can't change question type!`,
        description: `This question is used by the condition for the "${dependentQuestions[0].text}" question.`
      });
    }

    const dependentAlerts = getDependentAlerts(form, question);

    if (dependentAlerts.length > 0) {
      return this.openAlertDialog({
        title: `Can't change question type!`,
        description: `This question is used by the condition for the "${dependentAlerts[0].name}" alert.`
      });
    }

    // Calculate the next question options state.
    let questionOptions = question.questionOptions;

    if (!hasChoices(e.target.value)) {
      questionOptions = [];
    }
    else if (questionOptions.length < 1) {
      questionOptions = [createQuestionOption('', 0)];
    }

    this.setQuestionState({
      type: e.target.value,
      defaultAnswers: [],
      questionOptions
    });
  }

  handleRequiredChanged = e => {
    this.setQuestionState({required: e.target.checked, defaultAnswers: []});
  }

  handleConditionToggle = () => {
    const {editCondition} = this.state;

    if (editCondition) {
      this.setQuestionState({condition: null});
    }

    this.setState({editCondition: !editCondition});
  }

  handleOptionsChanged = (options, onComplete) => {
    this.setQuestionState({questionOptions: options}, onComplete);
  }

  handleDefaultAnswersChanged = defaultAnswers => {
    this.setQuestionState({defaultAnswers});
  }

  handleSetDefaultAnswer = e => {
    this.setState({editDefaultAnswers: e.target.checked});

    if (!e.target.checked) {
      this.handleDefaultAnswersChanged([]);
    }
  }

  handleSetCatalogTypes = e => {
    this.setState({restrictCatalogTypes: e.target.checked});

    if (!e.target.checked) {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'selectedTypes')
      }));
    }
  }

  handleAddCatalogType = (e, type) => {
    if (e.target.checked) {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta, {key: 'selectedTypes', value: type.id}]
      }));
    }
    else {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta
          .filter(metadata => metadata.key !== 'selectedTypes' || metadata.value !== type.id)
      }));
    }
  }

  toggleQuestionOptions = () => {
    this.setState(prevState => ({
      optionsOpen: !prevState.optionsOpen
    }));
  }

  handleCopyQuestion = () => {
    const {question, onAdd} = this.props;
    onAdd({...question, text: `Copy of ${question.text}`});
  }

  handleAddQuestionAbove = () => {
    const {question, onAdd} = this.props;
    onAdd(createQuestion(question.order - 1));
  }

  handleAddQuestionBelow = () => {
    const {question, onAdd} = this.props;
    onAdd(createQuestion(question.order));
  }

  toggleAdvancedSettings = () => {
    this.setState(prevState => ({
      showAdvancedSettings: !prevState.showAdvancedSettings
    }));
  }

  handleAllowSaveAnswersToggle = e => {
    const checked = e.target.checked;
    this.setState({allowSaveAnswers: checked});

    if (checked) {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'allowSaveAnswers')
      }));
    } else {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta, {key: 'allowSaveAnswers', value: 'false'}]
      }));
    }
  }

  handleGridConfigChange = ({width, height}) => {
    this.setState({gridWidth: width, gridHeight: height});
    this.setQuestionState(question => ({
      questionMeta: [
        ...question.questionMeta.filter(metadata => metadata.key !== 'gridWidth' && metadata.key !== 'gridHeight'),
        {key: 'gridWidth', value: width.toString()},
        {key: 'gridHeight', value: height.toString()}
      ]
    }));
  }

  handleDisplayInlineToggle = e => {
    const checked = e.target.checked;
    this.setState({displayInline: checked});

    if (checked) {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta.filter(metadata => metadata.key !== 'displayInline'), {key: 'displayInline', value: 'true'}]
      }));
    } else {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'displayInline')
      }));
    }
  }

  handleQuestionWidthChange = e => {
    const value = e.target.value;
    this.setState({questionWidth: value});

    if (value === '1' || value === '1.0') {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'questionWidth')
      }));
    } else {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta.filter(metadata => metadata.key !== 'questionWidth'), {key: 'questionWidth', value}]
      }));
    }
  }

  handleQuestionColourChange = e =>{
    const value = e; 
    this.setState({questionColour: value});

    if (value === '#000000') { //base case Default Color
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'questionColour')
      }))
    }
    else{ //Change color 
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta.filter(metadata => metadata.key !== 'questionColour'), {key: 'questionColour',value}]
      }))
    }
  }

  //handler function for the card colour
  handleCardColourChange = e => {
    const value = e;
    this.setState({cardColour: value});

    if (value === '#FFFFFF' || value === '#ffffff') {
      this.setQuestionState(question => ({
        questionMeta: question.questionMeta.filter(metadata => metadata.key !== 'cardColour')
      }));
    } else {
      this.setQuestionState(question => ({
        questionMeta: [...question.questionMeta.filter(metadata => metadata.key !== 'cardColour'), {key: 'cardColour', value}]
      }));
    }
  }

  render() {
    const {form, catalog, catalogTypes, question, validations, disableDelete} = this.props;
    const {editCondition, restrictCatalogTypes, editDefaultAnswers, dialog, optionsOpen, showAdvancedSettings, allowSaveAnswers, gridWidth, gridHeight, displayInline, questionWidth} = this.state;
    const selectedCatalogTypes = getQuestionMetadata(question, 'selectedTypes'); // Only used for single and multi-catalog questions.
    const isFirstQuestion = question.order === 0;

    return (
      <Container
        focusable
        form={form}
        disabled={disableDelete}
        dialogTitle="Delete this question?"
        dialogDescription="Are you sure you want to delete this question? This cannot be undone."
        ok="Delete"
        onBeforeOk={this.handleConfirmDelete}
        onOk={this.handleRemove}
        overlay={dialog && <AlertDialog onOk={this.closeAlertDialog} {...dialog} />}>
        <Header>
          <DragIcon>
            <Icon name="align-justify" size="1.5em" />
          </DragIcon>
          <HeaderContent>
            {form.published ? (
              <h4>{question.text}</h4>
            ) : (
              <QuestionTitleInput
                type="text"
                placeholder="Question Title"
                value={question.text}
                onChange={e => this.setQuestionState({text: e.target.value})}
                invalid={validations.text}
                validationText={validations.text}
              />
            )}
            {question.type === 'NUMERIC_GRID' && (
              <QuestionSubtitle>
                A multi number question formatted as a grid
              </QuestionSubtitle>
            )}
          </HeaderContent>
          <Input
            disabled={form.published}
            type="select" 
            value={question.type}
            onChange={this.handleQuestionTypeChange}
            invalid={validations.type}
            validationText={validations.type || ""}>
            <option value="">Question Type</option>
            <option value="TEXT">Text</option>
            <option value="NUMERIC">Number</option>
            <option value="MULTI_NUMERIC">Multi-number</option>
            <option value="NUMERIC_GRID">Number Grid</option>
            <option value="RADIO">Single-select</option>
            <option value="CHECKBOXES">Multi-select</option>
            <option value="BOOLEAN">Yes or No</option>
            <option value="IMAGE">Photo</option>
            <option value="DATETIME">Date &amp; Time</option>
            <option value="SIGNATURE">Signature</option>
            <option value="SCANNED_CODE">Code Scanner</option>
            {catalog.length > 0 && (
              <>
                <option value="CATALOG_RADIO">Single-catalog</option>
                <option value="CATALOG_CHECKBOX">Multi-catalog</option>
              </>
            )}
          </Input>
          {!form.published && (
            <>
              <QuestionOptionsButton id={`question-options-${question.id}`} tertiary onClick={this.toggleQuestionOptions}>
                <Icon name="chevron-down" />
              </QuestionOptionsButton>
              <Menu open={optionsOpen} relativeTo={`question-options-${question.id}`} onClose={this.toggleQuestionOptions}>
                <Menu.Option onClick={this.handleCopyQuestion}>Copy question</Menu.Option>
                <Menu.Option onClick={this.handleAddQuestionAbove}>Add question above</Menu.Option>
                <Menu.Option onClick={this.handleAddQuestionBelow}>Add question below</Menu.Option>
              </Menu>
            </>
          )}
        </Header>
        <Body>
          {hasChoices(question.type) && (
            <OptionsBuilder
              question={question}
              validations={validations.questionOptionsByValue}
              onChange={this.handleOptionsChanged}
              onChangeDefaultAnswers={this.handleDefaultAnswersChanged}
            />
          )}
          {question.type === 'NUMERIC_GRID' && (
            <NumericGridBuilder
              gridWidth={gridWidth}
              gridHeight={gridHeight}
              questionText={question.text}
              onChange={this.handleGridConfigChange}
            />
          )}
          {isCatalogQuestion(question.type) && (
            <>
              <Checkbox>
                <input
                  type="checkbox"
                  checked={restrictCatalogTypes}
                  onChange={this.handleSetCatalogTypes}
                />
                &nbsp;
                Restrict the available catalog types
              </Checkbox>
              {restrictCatalogTypes && (
                <SubConfiguration>
                  {catalogTypes.map(type => (
                    <SubConfiguration.Item key={type.id}>
                      <Checkbox>
                        <input
                          type="checkbox"
                          checked={selectedCatalogTypes.indexOf(type.id) >= 0}
                          onChange={e => this.handleAddCatalogType(e, type)}
                        />
                        &nbsp;
                        {type.name}
                      </Checkbox>
                    </SubConfiguration.Item>
                  ))}
                </SubConfiguration>
              )}
            </>
          )}
          <Checkbox>
            <input
              type="checkbox"
              checked={question.required}
              onChange={this.handleRequiredChanged}
            />
            &nbsp;
            This question is required
          </Checkbox>
          <Checkbox>
            <input
              disabled={form.published}
              type="checkbox"
              checked={question.context}
              onChange={e => this.setQuestionState({context: e.target.checked})}
            />
            &nbsp;
            Show on setup
          </Checkbox>
          {question.context && question.type!=="IMAGE" && question.type!=="SIGNATURE" && question.type!=="SCANNED_CODE" && <Checkbox>
            <input
                type="checkbox"
                checked={question.preview}
                onChange={e => this.setQuestionState({preview: e.target.checked})}
            />
            &nbsp;
            Set as Preview Question
          </Checkbox>}
          {question.preview && ( <PreviewOrderContainer>
            Set Preview Order:
              <Input type="number"
                     name="previewOrder"
                     value={question.previewOrder || 0}
                     onChange={e => this.setQuestionState({previewOrder: parseInt(e.target.value)})}/>
          </PreviewOrderContainer>)}
          <AdvancedSettingsToggle onClick={this.toggleAdvancedSettings}>
            <Icon name="chevron-right" style={{transform: showAdvancedSettings ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s'}} />
            &nbsp;
            Advanced Settings
          </AdvancedSettingsToggle>
          {showAdvancedSettings && (
            <AdvancedSettingsSection>
              {canSetDefaultAnswer(question) && !hasChoices(question.type) && (
                <>
                  <Checkbox>
                    <input
                      type="checkbox"
                      checked={editDefaultAnswers}
                      onChange={this.handleSetDefaultAnswer}
                    />
                    &nbsp;
                    Set a default answer
                  </Checkbox>
                  {editDefaultAnswers && (
                    <DefaultAnswers
                      question={question}
                      onChange={this.handleDefaultAnswersChanged}
                    />
                  )}
                </>
              )}
              <Checkbox>
                <input
                  type="checkbox"
                  checked={editCondition}
                  onChange={this.handleConditionToggle}
                />
                &nbsp;
                Only ask this question if...
              </Checkbox>
              {editCondition && (
                <SubConfiguration>
                  <SubConfiguration.Item>
                    <JsonLogicBuilder
                      expand
                      variables={getLogicBuilderVariables(form, catalog, question)}
                      value={typeof question.condition === 'string' ? JSON.parse(question.condition) : null}
                      onChange={condition => this.setQuestionState({condition: JSON.stringify(condition)})}
                    />
                  </SubConfiguration.Item>
                </SubConfiguration>
              )}
              <Checkbox>
                <input
                  type="checkbox"
                  checked={allowSaveAnswers}
                  onChange={this.handleAllowSaveAnswersToggle}
                />
                &nbsp;
                Allow answers to be saved
              </Checkbox>
              {question.type === 'RADIO' && (
                <Checkbox>
                  <input
                    type="checkbox"
                    checked={getQuestionMetadata(question, 'displayHorizontal').includes('true')}
                    onChange={e => {
                      const checked = e.target.checked;
                      this.setQuestionState(question => ({
                        questionMeta: checked
                          ? [...question.questionMeta.filter(metadata => metadata.key !== 'displayHorizontal'),
                             {key: 'displayHorizontal', value: 'true'}]
                          : question.questionMeta.filter(metadata => metadata.key !== 'displayHorizontal')
                      }));
                    }}
                  />
                  &nbsp;
                  Display Horizontal
                </Checkbox>
              )}
              {!isFirstQuestion && question.type !== 'NUMERIC_GRID' && (
                <Checkbox>
                  <input
                    type="checkbox"
                    checked={displayInline}
                    onChange={this.handleDisplayInlineToggle}
                  />
                  &nbsp;
                  Display inline with previous question
                </Checkbox>
              )}
              {question.type !== 'NUMERIC_GRID' && (
                <QuestionWidthContainer>
                  <div>
                    <label>Question width:</label>
                    <SegmentedControl>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '1' || questionWidth === '1.0'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '1'}})}
                    >
                      Full
                    </SegmentOption>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '0.75'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '0.75'}})}
                    >
                      3/4
                    </SegmentOption>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '0.67' || questionWidth === '0.66'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '0.67'}})}
                    >
                      2/3
                    </SegmentOption>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '0.5'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '0.5'}})}
                    >
                      1/2
                    </SegmentOption>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '0.33'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '0.33'}})}
                    >
                      1/3
                    </SegmentOption>
                    <SegmentOption
                      type="button"
                      active={questionWidth === '0.25'}
                      onClick={() => this.handleQuestionWidthChange({target: {value: '0.25'}})}
                    >
                      1/4
                    </SegmentOption>
                    </SegmentedControl>
                  </div>
                  {parseFloat(questionWidth) < 0.5 && (
                    <WidthWarning>
                      Questions narrower than 1/2 width will display at 1/2 width on phones
                    </WidthWarning>
                  )}
                </QuestionWidthContainer>
              )}
              <ColorPickerGroup>
                <Input 
                  id='backgroud-color-picker'
                  label='Question Color:'
                  labelPosition='start'
                  color={this.state.questionColour || "#000000"}
                  type='color'
                  invalid={!isValidCssColor(this.state.questionColour || "#000000")}
                  onChange={this.handleQuestionColourChange}
                />
                <Input
                  id='card-colour-picker'
                  label='Question Background Color:'
                  labelPosition='start'
                  color={this.state.cardColour || "#ffffff"}
                  type='color'
                  invalid={!isValidCssColor(this.state.cardColour || "#ffffff")}
                  onChange={this.handleCardColourChange}
                />
              </ColorPickerGroup>
              {(this.state.questionColour || '#000000').toLowerCase() === (this.state.cardColour || '#FFFFFF').toLowerCase() && (
                <ColorWarning>
                  <Icon name="alert-triangle" size="1em" />
                  &nbsp;
                  Warning: Question color and background color are the same. Question text may not be visible.
                </ColorWarning>
              )}
            </AdvancedSettingsSection>
          )}
        </Body>
      </Container>
    );
  }
}

function Container({form, ...props}) {
  if (form.published) {
    // Remove the props that are only meant for the DeletableCard component.
    const {disabled, dialogTitle, dialogDescription, ok, onBeforeOk, onOk, children, ...cardProps} = props;

    return (
      <Card {...cardProps}>
        <Card.Content>
          {children}
        </Card.Content>
      </Card>
    );
  }

  return <DeletableCard {...props} />;
}

function canSetDefaultAnswer(question) {
  return !question.required
    && (question.type === 'NUMERIC'
    || question.type === 'TEXT'
    || question.type === 'RADIO'
    || question.type === 'CHECKBOXES'
    || question.type === 'BOOLEAN');
}

//helper function to check if components in the advanced settings are not their default values
function hasAdvancedSettings(question) {
  const meta = key => getQuestionMetadata(question, key);
  return !!question.condition
    || question.defaultAnswers.length > 0
    || meta('allowSaveAnswers').includes('false')
    || meta('displayInline').includes('true')
    || (meta('questionWidth')[0] && meta('questionWidth')[0] !== '1' && meta('questionWidth')[0] !== '1.0')
    || (meta('questionColour')[0] && meta('questionColour')[0] !== '#000000')
    || (meta('cardColour')[0] && meta('cardColour')[0] !== '#FFFFFF' && meta('cardColour')[0] !== '#ffffff')
    || meta('displayHorizontal').includes('true');
}

function isCatalogQuestion(questionType) {
  return questionType === 'CATALOG_RADIO' || questionType === 'CATALOG_CHECKBOX';
}

function getDependentQuestions(form, question) {
  // Returns the questions from the form that depend on this question.
  const questionsWithConditions = [
    ...form.contextQuestions.filter(q => !!q.condition),
    ...form.questions.filter(q => !!q.condition)
  ].sort((a, b) => a.order - b.order);

  return questionsWithConditions
    .filter(q => parseVariables(JSON.parse(q.condition)).indexOf(question.id) >= 0);
}

function getDependentAlerts(form, question) {
  // Returns the alerts from the form that depend on this question.
  return form.alerts
    .filter(alert => parseVariables(JSON.parse(alert.expression)).indexOf(question.id) >= 0);
}

function getDependentElements(form, question) {
  return form.elements
    .filter(element => element.type === 'CALCULATION' || element.type === 'AGGREGATION')
    .filter(element => parseVariables(JSON.parse(element.data.formula)).indexOf(question.id) >= 0);
}

function getQuestionMetadata(question, key) {
  return question.questionMeta
    .filter(metadata => metadata.key === key)
    .map(metadata => metadata.value);
}

const Header = styled.div`
  display: flex;

  h4 {
    flex: 1;
    margin: 0.4rem 0 0 0;
    padding: 0;
    font-size: 1.125rem;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const QuestionSubtitle = styled.p`
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0;
`;

const DragIcon = SortableHandle(styled.div`
  cursor: grab;
  color: #b4b4b4;
  margin-top: 0.4rem;
  margin-right: 1rem;
`);

const QuestionTitleInput = styled(({className, ...inputProps}) => (
  <div className={className}>
    <Input {...inputProps} />
  </div>
))`
  flex: 1;
  padding: 0 1rem 0 0;
`;

const QuestionOptionsButton = styled(Button)`
  margin-right: -1rem;
  margin-left: 1rem;
`;

const Body = styled.div`
  margin: 1rem 0 0 2.5rem;

  > * {
    margin-top: 0.625rem;
  }
`;

export const Checkbox = styled(({children, ...props}) => (
  <div {...props}>
    <label>{children}</label>
  </div>
))``;

const AdvancedSettingsToggle = styled.div`
  cursor: pointer;
  color: #666;
  font-weight: 500;
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  user-select: none;
  
  &:hover {
    color: #333;
  }
`;

const AdvancedSettingsSection = styled.div`
  padding-left: 1rem;
  border-left: 2px solid #f0f0f0;
  margin-left: 0.5rem;

  > * {
    margin-top: 0.625rem;
  }
`;

const QuestionWidthContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  > div:first-child {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  label {
    font-weight: 500;
    color: #333;
    font-size: 0.9rem;
  }
`;

const WidthWarning = styled.div`
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
  margin-left: 0.5rem;
`;

const SegmentedControl = styled.div`
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  overflow: hidden;
  background: #ffffff;
`;

const SegmentOption = styled.button`
  padding: 0.375rem 0.75rem;
  border: none;
  background: ${props => props.active ? '#3b82f6' : '#ffffff'};
  color: ${props => props.active ? '#ffffff' : '#374151'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  border-right: 1px solid #d1d5db;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f3f4f6'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    z-index: 1;
    position: relative;
  }
`;

const ColorPickerGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  align-items: center;

  > div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  input[type="color"] {
    width: 60px;
    height: 32px;
    border: 1px solid #000000;
    border-radius: 4px;
    cursor: pointer;
  }
`;

const ColorWarning = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 0.375rem;
  color: #856404;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const PreviewOrderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export default QuestionBuilder;